import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  Input,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { I2gMoneyWebview } from '../../../../settings-data/i2g-money.data';
import { SettingsDataStore } from '../../../../settings-data/settings-data.store';
import { map, takeUntil, pairwise } from 'rxjs/operators';
import { WindowRefService } from 'i2g-ng-utils';
import { SettingsDataDispatcher } from '../../../../settings-data/settings-data.dispatcher';
import { NavigationService } from '../../../../navigation/navigation.service';
import { SETTINGS_CLIENT_PAYMENT_OPTIONS_ROUTE } from '../../../../settings/settings.data';
import { SETTINGS_ROUTE } from '../../../../main/main.data';
import { TrackingScreenNamesEnum } from '../../../../tracking/tracking.data';
import { HomeDataDispatcher } from '../../../../home-data';

@Component({
  selector: 'app-i2g-money-onboarding-dialog',
  templateUrl: './i2g-money-onboarding-dialog.component.html',
  styleUrls: ['./i2g-money-onboarding-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class I2gMoneyOnboardingDialogComponent implements OnInit, OnDestroy {
  @Input() trackingScreenName: string;

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  webview$: Observable<I2gMoneyWebview>;

  _windowRef: Window;

  constructor(
    private _windowRefService: WindowRefService,
    private _store: SettingsDataStore,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _settingsDataStore: SettingsDataStore,
    private _navigationService: NavigationService,
    private _homeDataDispatcher: HomeDataDispatcher
  ) {
    this._windowRef = this._windowRefService.nativeWindow;
  }

  ngOnInit() {
    this.webview$ = this._store
      .getI2gMoney()
      .pipe(map((value) => value.webview));

    // add event listener to listen to callbacks
    // from iframe
    this._windowRef.parent.addEventListener(
      'message',
      this.handleMessagesFromIframe.bind(this)
    );

    // observe unit context to ensure we turn the toggle on once application is approved;
    this._settingsDataStore
      .getI2gMoney()
      .pipe(pairwise(), takeUntil(this._destroyed))
      .subscribe(([prevValue, curValue]) => {
        const wasEnabledPreviously =
          prevValue.context &&
          prevValue.context.application &&
          prevValue.context.application.status &&
          prevValue.context.application.status === 'approved';

        const isEnabledCurrently =
          curValue.context &&
          curValue.context.application &&
          curValue.context.application.status &&
          curValue.context.application.status === 'approved';

        if (!wasEnabledPreviously && isEnabledCurrently) {
          this._settingsDataDispatcher.updateI2gMoneyContext(true);
        }
      });
  }

  ngOnDestroy() {
    // remove event listener
    this._windowRef.parent.removeEventListener(
      'message',
      this.handleMessagesFromIframe
    );

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  handleMessagesFromIframe(event: MessageEvent) {
    if (event && event.data) {
      // if asking to close iframe
      if (event.data === 'close-i2g-money-onboarding') {
        this.closeOnboardingAndFetchContext();
      }

      if (event.data === 'view-i2g-bank-account') {
        this.closeOnboardingAndFetchContext();

        // navigate to client payment options
        this._navigationService.goTo([
          SETTINGS_ROUTE,
          SETTINGS_CLIENT_PAYMENT_OPTIONS_ROUTE,
        ]);
      }
    }
  }

  closeOnboardingAndFetchContext() {
    // fetch unit context again
    this._settingsDataDispatcher.fetchI2gMoneyContext();

    // refresh task list if origin view is FTU task list
    if (this.trackingScreenName === TrackingScreenNamesEnum.home) {
      this._homeDataDispatcher.fetchTaskV4();
    }

    // close the dialog
    this._settingsDataDispatcher.hideI2gMoneyOnboarding();
  }
}
